import React from 'react';
import { Button } from '@roger/ui-components';
import { Undo2 } from 'lucide-react';

import { taskRestoreBlockedReason, type RestorableTask } from './taskRestore';

interface RestoreTaskButtonProps {
  task: RestorableTask;
  /** Name of the task, for the icon-only variant's accessible label. */
  taskLabel: string;
  restoring: boolean;
  onRestore: () => void;
  /**
   * Icon-only, for the task table's action column. The detail view uses the
   * labelled form, where there is room to print the refusal rather than hide
   * it behind a hover.
   */
  compact?: boolean;
}

/**
 * Restore a deleted task, or say why it cannot be restored.
 *
 * A control that is disabled without a reason is the failure this feature is
 * meant to avoid, so the reason is always attached: as text beside the button
 * in the detail view, and as a tooltip on the wrapper in the table (a disabled
 * button does not reliably surface its own title).
 */
export const RestoreTaskButton: React.FC<RestoreTaskButtonProps> = ({
  task,
  taskLabel,
  restoring,
  onRestore,
  compact = false,
}) => {
  const blockedReason = taskRestoreBlockedReason(task);
  const disabled = restoring || blockedReason !== null;

  if (compact) {
    return (
      <span title={blockedReason ?? undefined}>
        <Button
          variant='ghost'
          size='sm'
          aria-label={`Restore ${taskLabel}`}
          title={blockedReason ?? undefined}
          disabled={disabled}
          onClick={onRestore}
          className='h-8 w-8 p-0'
        >
          {restoring ? (
            <div className='h-3 w-3 animate-spin rounded-full border-b-2 border-current' />
          ) : (
            <Undo2 className='h-4 w-4' />
          )}
        </Button>
      </span>
    );
  }

  return (
    <div className='flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2'>
      <Button
        variant='outline'
        size='sm'
        disabled={disabled}
        onClick={onRestore}
        className='whitespace-nowrap'
      >
        <Undo2 className='mr-1.5 h-3.5 w-3.5' />
        {restoring ? 'Restoring...' : 'Restore'}
      </Button>
      {blockedReason && (
        <span className='text-xs text-muted-foreground'>{blockedReason}</span>
      )}
    </div>
  );
};
